<template>
  <div>
    <Row>
      <i-col span="24">
        <h2 style="margin-bottom: 12px; color: cornflowerblue;">{{ idx + 1 }}. New Quick Fact</h2>
      </i-col>
    </Row>

    <i-form ref="form" :model="value" :rules="rules" label-position="top">
      <i-form-item label="Name" prop="name">
        <i-input v-model.trim="value.name"></i-input>
      </i-form-item>

      <Row>
        <i-col span="12">
          <i-form-item label="Country" prop="iso3" style="margin-right:6px;">
            <CountrySelect v-model="value.iso3" :filterable="true"></CountrySelect>
          </i-form-item>
        </i-col>
        <i-col span="12">
          <i-form-item label="Year" prop="year">
            <i-select v-model.number="value.year" filterable>
              <i-option v-for="year in years" :key="year" :value="year">
                {{
                year
                }}
              </i-option>
            </i-select>
          </i-form-item>
        </i-col>
      </Row>
      <Row>
        <i-col span="12">
          <i-form-item label="Displacement" prop="displacement_type" style="margin-right:6px;">
            <i-select v-model="value.displacement_type">
              <i-option value="Conflict">Conflict</i-option>
              <i-option value="Disaster">Disaster</i-option>
              <i-option value="Development">Development</i-option>
            </i-select>
          </i-form-item>
        </i-col>
        <i-col span="12">
          <i-form-item label="Type" prop="type">
            <i-select v-model="value.type">
              <i-option value="IDPs (Stock)">IDPs (Stock)</i-option>
              <i-option value="New Displacement (Flow)">New Displacement (Flow)</i-option>
              <i-option value="Returnees (Stock)">Returnees (Stock)</i-option>
              <i-option value="Locally Integrated IDPs (Stock)">Locally Integrated IDPs (Stock)</i-option>
              <i-option value="IDPs Settled Elsewhere (Stock)">IDPs Settled Elsewhere (Stock)</i-option>
              <i-option
                value="People Displaced Across Borders (Stock)"
              >People Displaced Across Borders (Stock)</i-option>
              <i-option value="Multiple Displacement (Flow)">Multiple Displacement (Flow)</i-option>
              <i-option value="Births (Flow)">Births (Flow)</i-option>
              <i-option value="Deaths (Flow)">Deaths (Flow)</i-option>
              <i-option value="Return (Flow)">Return (Flow)</i-option>
              <i-option
                value="Failed Return / Returnee Displacement (Flow)"
              >Failed Return / Returnee Displacement (Flow)</i-option>
              <i-option value="Local Integration (Flow)">Local Integration (Flow)</i-option>
              <i-option value="Failed Local Integration (Flow)">Failed Local Integration (Flow)</i-option>
              <i-option value="Relocation Elsewhere (Flow)">Relocation Elsewhere (Flow)</i-option>
              <i-option
                value="Failed Relocation Elsewhere (Flow)"
              >Failed Relocation Elsewhere (Flow)</i-option>
              <i-option value="Cross-border Flight (Flow)">Cross-border Flight (Flow)</i-option>
              <i-option value="Cross-border Return (Flow)">Cross-border Return (Flow)</i-option>
            </i-select>
          </i-form-item>
        </i-col>
      </Row>

      <Row v-if="!isFlow">
        <i-col span="12">
          <i-form-item label="Stock Date" prop="data.stock_date.date">
            <AccuracyDatePicker v-model="value.data.stock_date"></AccuracyDatePicker>
          </i-form-item>
        </i-col>
      </Row>

      <Row v-if="isFlow">
        <i-col span="12">
          <i-form-item label="Start Date" prop="data.start_date.date" style="margin-right:6px;">
            <AccuracyDatePicker v-model="value.data.start_date"></AccuracyDatePicker>
          </i-form-item>
        </i-col>
        <i-col span="12">
          <i-form-item label="End Date">
            <AccuracyDatePicker v-model="value.data.end_date"></AccuracyDatePicker>
          </i-form-item>
        </i-col>
      </Row>

      <Row>
        <Row>
          <i-col span="6">
            <i-form-item label="Qualifier" style="margin-right:6px;">
              <i-select v-model="value.data.qualifier" @on-change="calculate">
                <i-option value="approximately">Approximately</i-option>
                <i-option value="less than">Less than</i-option>
                <i-option value="more than">More than</i-option>
                <i-option value="total">Total</i-option>
              </i-select>
            </i-form-item>
          </i-col>

          <i-col span="4">
            <i-form-item
              label="Reported Figure"
              style="margin-right:6px;"
              prop="data.reported_figure"
            >
              <i-input v-model.number="value.data.reported_figure" number @on-change="calculate"></i-input>
            </i-form-item>
          </i-col>

          <i-col span="4">
            <i-form-item label="Unit" style="margin-right:6px;" prop="data.unit">
              <i-select v-model="value.data.unit" @on-change="calculate">
                <i-option value="Person">Person</i-option>
                <i-option value="Household">Household</i-option>
              </i-select>
            </i-form-item>
          </i-col>

          <i-col span="4">
            <i-form-item label="Figure" style="margin-right:6px;" prop="data.figure">
              <i-input v-model.number="value.data.figure" readonly></i-input>
            </i-form-item>
          </i-col>

          <i-col span="6">
            <i-form-item label="Term" prop="data.term">
              <i-select v-model="value.data.term">
                <i-option value="Displaced">Displaced</i-option>
                <i-option value="Evacuated">Evacuated</i-option>
                <i-option value="Destroyed Housing">Destroyed Housing</i-option>
                <i-option value="Partially Destroyed Housing">Partially Destroyed Housing</i-option>
                <i-option value="Uninhabitable Housing">Uninhabitable Housing</i-option>
                <i-option value="Forced to Flee">Forced to Flee</i-option>
                <i-option value="Homeless">Homeless</i-option>
                <i-option value="In Relief Camp">In Relief Camp</i-option>
                <i-option value="Sheltered">Sheltered</i-option>
                <i-option value="Relocated">Relocated</i-option>
                <i-option value="Affected">Affected</i-option>
                <i-option value="Returns">Returns</i-option>
                <i-option value="Multiple/Other">Multiple/Other</i-option>
              </i-select>
            </i-form-item>
          </i-col>
        </Row>

        <Row>
          <!-- Household Size -->
          <i-form-item label="Household Size" v-show="value.data.unit === 'Household'">
            <HouseholdSize
              v-model="value.household_size"
              :conditions="{ iso3: value.iso3, year: value.year }"
              :iso3="value.iso3"
              :year="value.year"
              :recalculate="value.data.unit === 'Household'"
              @change="calculate"
            ></HouseholdSize>
          </i-form-item>
        </Row>
      </Row>

      <!--
        <i-form-item label="Household Size">
          <HouseholdSize v-model="value.household_size" :conditions="{iso3: value.iso3, year: value.year}"></HouseholdSize>
        </i-form-item>
      -->
      <!-- Master Fact -->
      <i-form-item label="Master Fact">
        <MasterFactAssociation
          :id="value.parent_id"
          :role="value.parent_role"
          :conditions="{
            iso3: value.iso3,
            type: value.type,
            displacement_type: value.displacement_type,
            year: value.year,
          }"
          @change="
            ({id, role}) => {
              value.parent_id = id;
              value.parent_role = role;
            }
          "
        ></MasterFactAssociation>
      </i-form-item>

      <!-- Event -->
      <i-form-item label="Event">
        <EventAssociation
          :id="value.event_id"
          :role="value.event_role"
          @change="
            ({ id, role }) => {
              value.event_id = id;
              value.event_role = role;
            }
          "
        ></EventAssociation>
      </i-form-item>

      <i-form-item>
        <slot></slot>
        <!-- <i-button @click="debugQuickFact">Debug Quick Fact</i-button> -->
      </i-form-item>
    </i-form>
  </div>
</template>

<script>
import _ from "lodash";
import CountrySelect from "@/components/country_select.vue";
import AccuracyDatePicker from "@/components/accuracy_date_picker.vue";
// import FigureFields from "@/components/figure_fields.vue";
import HouseholdSize from "@/components/household_size.vue";
import EventAssociation from "@/components/event_association.vue";
import MasterFactAssociation from "@/components/master_fact_association.vue";

export default {
  Name: "FactQuickForm",
  components: {
    AccuracyDatePicker,
    // FigureFields,
    HouseholdSize,
    EventAssociation,
    MasterFactAssociation,
    CountrySelect,
  },
  props: ["value", "idx"],
  data() {
    return {
      allCountries: [],
      rules: {
        name: [
          {
            required: true,
            message: "Name is required",
            trigger: "blur",
          },
          {
            min: 3,
            message: "Length should be more than 3",
            trigger: "blur",
          },
        ],
        year: {
          required: true,
          type: "integer",
          message: "Year is required",
        },
        iso3: {
          required: true,
          message: "Country type is required",
        },
        displacement_type: {
          required: true,
          message: "Displacement type is required",
        },
        type: {
          required: true,
          message: "Fact type is required",
        },
        "data.stock_date.date": {
          required: true,
          message: "Stock date is required",
        },
        "data.start_date.date": {
          required: true,
          message: "Start date is required",
        },
        "data.reported_figure": {
          required: true,
          type: "integer",
          min: 0,
          message: "Reported figure is required",
        },
        "data.figure": {
          required: true,
          type: "integer",
          min: 0,
          message: "Reported figure is required",
        },
        "data.unit": {
          required: true,
          message: "Unit is required",
        },
        "data.term": {
          required: true,
          message: "Term is required",
        },
      },
    };
  },
  computed: {
    // Returns a list of years starting from the current year
    // used to fillup the select field
    years() {
      const currYear = new Date().getFullYear();
      return _.range(currYear, 1950, -1);
    },

    // Returns true if the fact is a flow type
    // used to determine the type of date fields
    isFlow() {
      return _.includes(this.value.type, "Flow");
    },
  },
  methods: {
    // Debug method
    debugQuickFact() {
      this.$refs["form"].validate(valid => {
        console.log(valid);
        if (valid) {
          console.log("Valid");
        } else {
          console.log("Not valid");
        }
      });
      console.log(this.value);
    },
    calculate() {
      if (this.value.data.unit === "Household") {
        this.value.data.figure = Math.round(
          this.value.data.reported_figure * this.value.household_size.figure
        );
      } else {
        this.value.data.figure = this.value.data.reported_figure;
      }
    },
  },
};
</script>

<style lang="css"></style>
